/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React, { useEffect } from "react";
import styles from "./PricingCardContent.module.css";
import { formattedPlanName } from "lib/plans/plans";
import { formatNumber } from "lib/intl/number";
import { Team_Service_Tiers } from "lib/types/graphql";
import type { UserServiceTier } from "page-components/PricingPage/plans/api/fetchDefaultV2UserServiceTier";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useWindowSize } from "@reach/window-size";
import cn from "classnames";
import { IconCoins } from "components/Icons/IconCoins";
import { Origin } from "./PricingCards";
import { Badge } from "components/Badge/Badge";

type TeamServiceTier = Pick<
  Team_Service_Tiers,
  | "id"
  | "name"
  | "base_monthly_price_dollars_cents"
  | "included_nanocredits"
  | "nanocredits_cost_cents"
  | "max_private_dashboards"
  | "max_private_queries"
  | "csv_downloads_per_month"
  | "can_use_crud_endpoints"
  | "allow_private_queries_as_views"
  | "api_calls_per_minute"
  | "max_folders"
  | "max_query_event_retention_days"
>;

export const PricingCardContent = (props: {
  serviceTier: TeamServiceTier | UserServiceTier;
  origin: Origin;
  isCardOpen: boolean;
  onDropdownClick: () => void;
  setIsCardOpen: (isCardOpen: boolean) => void;
  badgeText?: string;
  isRecommended?: boolean;
  button: React.ReactNode;
}) => {
  const {
    name,
    base_monthly_price_dollars_cents,
    included_nanocredits,
    nanocredits_cost_cents,
    max_private_dashboards,
    max_private_queries,
    csv_downloads_per_month,
    can_use_crud_endpoints,
    allow_private_queries_as_views,
    api_calls_per_minute,
    max_folders,
    max_query_event_retention_days,
  } = props.serviceTier;

  const {
    origin,
    isCardOpen,
    onDropdownClick,
    setIsCardOpen,
    badgeText,
  } = props;

  const { width } = useWindowSize();
  const isMobile = width < 1000;

  useEffect(() => {
    // close features if user is on mobile
    if (isMobile) {
      setIsCardOpen(false);
    }

    // open features if user is not on mobile and pricing card is being shown
    // on create team flow
    if (!isMobile && origin === "new_team") {
      setIsCardOpen(true);
    }
  }, [isMobile]);

  return (
    <div
      className={cn(
        styles.card,
        styles[`origin-${origin}`],
        props.isRecommended && styles.recommended
      )}
    >
      {props.isRecommended && (
        <div className={styles.recommendedBadge}>Recommended for you</div>
      )}

      <div className={styles.intro}>
        <div className={styles.planName}>
          {formattedPlanName(name)}
          {badgeText && (
            <Badge size="M" color="brand-orange" variant="filled">
              {badgeText}
            </Badge>
          )}
        </div>

        <div className={styles.description}>{renderDescription(name)}</div>
      </div>

      <div className={styles.price}>
        <h2>${base_monthly_price_dollars_cents / 100}</h2>
        <sub>/mo</sub>
      </div>

      <hr />

      <ul className={styles.details}>
        <li>
          <div className={styles.credits}>
            <b>{formatNumber(included_nanocredits / 1000000000)}</b>
            <span>&nbsp;Credits</span>
            <Tooltip
              label={
                <>
                  Credits allow you to flexibly decide how to use Dune’s most
                  key functionality, <br />
                  so you can freely choose how to trade off query executions
                  with execution performance
                  <br />
                  and API data export volumes.
                </>
              }
              position="above-center"
              hidden={isMobile}
            >
              <div>
                <IconCoins />
              </div>
            </Tooltip>
          </div>
        </li>

        {(!isMobile || isCardOpen) && (
          <>
            <li>
              <b>${(nanocredits_cost_cents * 100000000).toFixed(2)}</b>
              <span>&nbsp;/extra 10 credits</span>
            </li>

            <li>
              <b>{formatNumber(csv_downloads_per_month ?? 0)}</b>
              <span>&nbsp;CSV exports per month</span>
            </li>
          </>
        )}

        {isCardOpen && (
          <>
            <li>
              <b>{formatNumber(max_private_dashboards ?? 0)}&nbsp;</b>
              <span>
                {max_private_dashboards === 1
                  ? `Private dashboard`
                  : `Private dashboards`}
              </span>
            </li>
            <li>
              <b>{formatNumber(max_private_queries ?? 0)}</b>
              <span>&nbsp;Private queries</span>
            </li>
            <li>
              <b>{max_query_event_retention_days} days</b>{" "}
              <span>version history</span>
            </li>
            {api_calls_per_minute && (
              <li>
                <b>{formatNumber(api_calls_per_minute ?? 0)}</b>
                <span>&nbsp;API calls per minute</span>
              </li>
            )}
            <li>
              <b>{formatNumber(max_folders ?? 0)}</b>
              <span>&nbsp;Folders</span>
            </li>
            {/* TODO: Hardcoded, use data from DB */}
            <li>
              <span>
                Data uploads
                {name === "community" && (
                  <>
                    &nbsp;
                    <Badge size="M" variant="filled" color="neutral">
                      Coming soon
                    </Badge>
                  </>
                )}
              </span>
            </li>
            <li>
              {renderDataStorage(name)}{" "}
              <span>
                data storage
                {name === "community" && (
                  <>
                    &nbsp;
                    <Badge size="M" variant="filled" color="neutral">
                      Coming soon
                    </Badge>
                  </>
                )}
              </span>
            </li>
            {can_use_crud_endpoints && (
              <li>
                <span>CRUD queries API access</span>
              </li>
            )}
            {allow_private_queries_as_views && (
              <li>
                <span>Private query views</span>
              </li>
            )}
          </>
        )}
      </ul>

      <div className={styles.actions}>
        {props.button}

        {(origin === "pricing" || isMobile) && (
          <button onClick={onDropdownClick} className={styles.showPlanDetails}>
            {isCardOpen ? "Show fewer features" : "Show all features"}
          </button>
        )}
      </div>
    </div>
  );
};

function renderDescription(name: string) {
  switch (name) {
    case "plus":
      return "Query and track faster.";
    case "premium":
      return "Grow with integrated crypto data.";
    default:
      return "Build and share with the Dune community.";
  }
}

function renderDataStorage(name: string) {
  switch (name) {
    case "community":
      // Free team or free user
      return <b>10 MB</b>;
    case "plus":
      return <b>15 GB</b>;
    case "premium":
      return <b>50 GB</b>;
    default:
      return <></>;
  }
}
